import React, {useState} from 'react';
import {findProductsByQueryString} from "../../utils/requestUtils";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import GenericAsyncSelect from "../common/AsyncSelect";
import ProductOptionComponent from "./ProductOptionComponent";
import ProductAvailability from "./ProductAvailability";

/**
 * ProductSelectAsync Component
 * 
 * An asynchronous product selection component with search functionality and availability checking.
 * 
 * @param {Object} props
 * @param {Function} props.onItemClick - Callback when a product is selected
 * @param {Function} props.onClose - Callback when the component is closed
 * @param {string} props.placeholder - Placeholder text for the select
 * @param {Object} props.value - Current selected value (for controlled component)
 * @param {Function} props.onRequestRentProduct - Callback for rent product requests
 * @param {Object} props.prefilledProduct - Product object to prefill the select (for editing existing data)
 * @param {Object} props.style - Style object to apply to the select component (width for control, menuWidth for dropdown menu)
 * @param {number} props.index - Index of the rental item (for onRequestRentProduct callback)
 * 
 * @example
 * // Basic usage
 * <ProductSelectAsync onItemClick={handleProductSelect} />
 * 
 * @example
 * // With prefilled product data (for editing)
 * <ProductSelectAsync 
 *   onItemClick={handleProductSelect}
 *   prefilledProduct={{id: 1, code: "P001", name: "Product Name"}}
 *   style={{width: "200px", menuWidth: "300px"}}
 * />
 */
const ProductSelectAsync = (props) => {
    const {onItemClick, onClose, placeholder = "Selecione um produto...", value = null, onRequestRentProduct, prefilledProduct = null, style = {}, index = null, dataTest = "product-async-select", maintainInputValue = true, onInputValueChange = null, showProductAvailability = false} = props;
    const [, setLoading] = useRecoilState(loadingAtom);
    const [productAvailability, setProductAvailability] = useState(undefined);

    // Load options function for AsyncSelect
    const loadOptions = async (inputValue) => {
        // Clear data if less than 3 characters, search if 3 or more
        if (!inputValue || inputValue.trim().length < 3) {
            return [];
        }
        
        try {
            setLoading(true);
            const response = await findProductsByQueryString(inputValue.trim());
            
            // Transform the response to match react-select format
            const options = response.map(product => {
                // Debug: Log the product data from API
                console.log('ProductSelectAsync - API product:', product);
                console.log('ProductSelectAsync - product.code:', product.code, 'type:', typeof product.code);
                
                // Handle cases where code might be null, undefined, or 0
                const code = product.code && product.code > 0 ? product.code : '';
                const label = code ? `${code} - ${product.name}` : product.name;
                
                const option = {
                    showProductAvailability: showProductAvailability,
                    value: product.id,
                    label: label,
                    product: product, // Keep the full product object for reference
                    onAvailabilityClick: handleAvailabilityClick // Pass the handler
                };
                
                // Debug: Log the created option
                console.log('ProductSelectAsync - created option:', option);
                
                return option;
            });
            
            return options;
        } catch (error) {
            console.error('Error fetching products:', error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const handleAvailabilityClick = (product) => {
        setProductAvailability(product);
    };

    const closeAvailability = () => {
        setProductAvailability(undefined);
    };

    const handleRequestRentProduct = (productId, date, days, quantity) => {
        if (onRequestRentProduct) {
            onRequestRentProduct(productId, date, days, quantity, index);
            setProductAvailability(undefined);
        }
    };

    // Create prefilled value if product data is provided
    const getPrefilledValue = () => {
        if (prefilledProduct) {
            // Handle cases where code might be null, undefined, or 0
            const code = prefilledProduct.code && prefilledProduct.code > 0 ? prefilledProduct.code : '';
            const label = code ? `${code} - ${prefilledProduct.name}` : prefilledProduct.name;
            
            return {
                showProductAvailability: showProductAvailability,
                value: prefilledProduct.id,
                label: label,
                product: prefilledProduct,
                onAvailabilityClick: handleAvailabilityClick
            };
        }
        return value;
    };

    return (
        <>
            <GenericAsyncSelect
                loadOptions={loadOptions}
                onItemClick={onItemClick}
                onClose={onClose}
                placeholder={placeholder}
                value={getPrefilledValue()}
                dataTest={dataTest}
                customOptionComponent={ProductOptionComponent}
                style={style}
                maintainInputValue={maintainInputValue}
                onInputValueChange={onInputValueChange}
            />
            {productAvailability && (
                <ProductAvailability
                    onClose={closeAvailability}
                    product={productAvailability}
                    onRequestRentProduct={handleRequestRentProduct}
                />
            )}
        </>
    );
};

export default ProductSelectAsync; 